import React, { useEffect, ReactNode } from 'react';
import { ChatItemProps, ChatMessage, ProChat } from '@ant-design/pro-chat';
import { Button, Input, Tooltip } from 'antd';
import { CloseCircleFilled, CopyOutlined } from '@ant-design/icons';
import { useAgentSelector } from '../../hooks/useAgentStore';
import './ChangeRequestCopilot.less';
import { ReactComponent as AISvg } from '../../assets/ai.svg';
import { ReactComponent as LoaderSvg } from '../../assets/chat-loader.svg';
import { ReactComponent as SendButtonSvg } from '../../assets/send-button.svg';
import { ChecklistItem } from '../../interfaces/changeRequest';
import { Notify } from '../../services/notificationService';
import { useCopilotChat } from '../../hooks/useCopilotChat';
import { getReadonlyElementTitle } from '../../services/subscription';
import {
  CopilotChatMessageStatus,
  CopilotChatRole,
} from '../../enums/copilotChat';
import {
  ContentObjectType,
  CopilotChatMessageResponse,
} from '../../interfaces/copilotChat';
import TextMessage from '../CopilotBase/TextMessage';
import ChatTitle from '../CopilotBase/ChatTitle';

interface CopilotProps {
  editingRecord: ChecklistItem;
  onUpdateAnswer: (answer: string) => void;
  uniqueKey: number;
}

const ChangeRequestCopilot: React.FC<CopilotProps> = ({
  editingRecord,
  onUpdateAnswer,
  uniqueKey,
}) => {
  const { data } = useAgentSelector((state) => state.changeRequest);
  const threadRun = {
    context: `## Instructions\n
                  - Use the following context when answering questions in this thread:\n
                  - The user is completing a checklist for the following change request: \n ${data?.result?.summary || ''}\n
                  - One of the checklist questions is: ${editingRecord.question} \n
                  Your role is to provide assistance answering this specific question. \n
                  When asked, please suggest typical solutions used by Salesforce customers, or follow-up questions to ask to determine the appropriate answer. 
                  Respond concisely. Limit your response exclusively to the specific checklist question`,
  };
  const assistantType = {
    assistant_type: 'ca-copilot',
  };
  const {
    chatRef,
    currentMessage,
    setCurrentMessage,
    isSending,
    handleRequest,
    handleSendMessage,
    handleChange,
    onChatChange,
    org,
  } = useCopilotChat({
    assistantType: assistantType,
    threadRun: threadRun,
    filterMessages: (msgs: CopilotChatMessageResponse[]) => {
      const filteredMsgs = msgs.filter((msg) => {
        if (typeof msg.content === 'string') {
          return true;
        }
        const unsupportedTypes = [ContentObjectType.TOOL_CALL];
        const isUnsupported =
          typeof msg.content === 'object' &&
          unsupportedTypes.includes(msg.content.type);
        return !isUnsupported;
      });
      return filteredMsgs;
    },
    setLoadingCallback: (state: boolean) => setLoadingStateCallback(state),
  });

  useEffect(() => {
    chatRef.current?.clearMessage();
    const chatMessage = chatRef.current?.getChatMessages();
    if (chatMessage?.length === 0) {
      setCurrentMessage('Suggest an answer for this question.');
    }
  }, [uniqueKey, editingRecord]);

  const updateAnswer = async (answer: string) => {
    onUpdateAnswer(answer);
  };

  /**
   * Handle uniqueKey up event for the textarea.
   * @param e - Keyboard event
   */
  const handleKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e?.key === 'Enter' && !e?.shiftKey && currentMessage?.trim()) {
      handleSendMessage(currentMessage);
    }
  };

  const setLoadingStateCallback = (_state: boolean) => {
    // Implement loading state
  };

  return (
    <div id="cr-copilot" className="copilot cr-copilot">
      <ProChat
        chatRef={chatRef}
        helloMessage={
          <div>
            <AISvg />
            <span>Welcome! How may I help you?</span>
          </div>
        }
        chatItemRenderConfig={{
          titleRender: (props: ChatItemProps, defaultDom: ReactNode) => {
            return <ChatTitle props={props} defaultDom={defaultDom} />;
          },

          /* 
            Note:
            `actionsRender` and `contentRender` only re-renders when messages are appended or modified.
            It does not re-render external variables changes.
          */
          contentRender: (props: ChatItemProps, defaultDom: ReactNode) => {
            const msg = props.originData as ChatMessage;
            if (msg) {
              if (msg.error) {
                const errorContent = msg.error.message.replace(
                  'response error, status:',
                  ''
                );
                return (
                  <div
                    id={msg.id}
                    className="ant-alert ant-alert-error message-error"
                    role="alert"
                  >
                    <CloseCircleFilled className="ant-alert-icon" />
                    <div className="ant-alert-content">
                      <div className="ant-alert-message">{errorContent}</div>
                    </div>
                  </div>
                );
              }
              const originalMsg = msg.extra?.originData;
              const isCompletedAssistantMsg =
                msg.role === CopilotChatRole.ASSISTANT &&
                typeof msg.content === 'string' &&
                originalMsg &&
                originalMsg.status === CopilotChatMessageStatus.COMPLETED;
              return (
                <div>
                  <TextMessage msg={msg} editableContent={defaultDom} />
                  {isCompletedAssistantMsg && (
                    <div className="action-buttons">
                      <Button
                        type="default"
                        onClick={() => {
                          updateAnswer(msg.content as string);
                        }}
                      >
                        Use as answer
                      </Button>
                      <CopyOutlined
                        title="Copy"
                        onClick={() => {
                          navigator.clipboard.writeText(msg.content as string);
                          Notify.success('Copied to clipboard');
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            }
          },
        }}
        request={(chatMessages: ChatMessage[]) => {
          return handleRequest(chatMessages);
        }}
        locale="en-US"
        showTitle
        userMeta={{
          title: 'User',
        }}
        assistantMeta={{
          title: 'Cirra AI',
        }}
        onChatsChange={(msgs: ChatMessage[]) => onChatChange(msgs)}
      />
      <div className="copilot-form">
        <div className="copilot-form-height">
          <Tooltip
            title={getReadonlyElementTitle()}
            placement="right"
            destroyOnHidden={false}
          >
            <Input.TextArea
              className="text-area-input"
              value={currentMessage}
              onKeyUp={handleKeyUp}
              onChange={handleChange}
              placeholder="Enter your request"
              disabled={isSending || !org?.active}
            />
            <Button
              type="primary"
              onClick={() => handleSendMessage(currentMessage)}
              disabled={isSending || !currentMessage.trim() || !org?.active}
            >
              {isSending ? <LoaderSvg /> : <SendButtonSvg />}
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default ChangeRequestCopilot;
